import { isEmail } from 'validator'
import { create, enforce, omitWhen, only, test } from 'vest'

import {
  getClosestElement,
  getElementChild,
  getElementChildren,
  getElements,
} from '../Utility/Dom'
import { IS_EMPTY, IS_INVALID_EMAIL } from '../Utility/FormErrorCodes'
import { IS_BAD, IS_LOADING } from '../Utility/State'
import { isValidJson } from '../Validation/Json'

enforce.extend({
  isEmail,
  isSingleFile: (value) => {
    return value.name !== ''
  }
})

export default class FormValidationService {
  private forms: NodeListOf<HTMLFormElement>
  private validationSuites: { formId: string; formValidationSuiteInstance: ReturnType<typeof create> }[]

  constructor () {
    this.forms = getElements('form') as NodeListOf<HTMLFormElement>
    this.validationSuites = []
  }

  public init (): void {
    this.forms.forEach((form) => {
      const formId = form.getAttribute('data-id')
      const formElements = getElementChildren(form, 'form-element')
      const formValidationObjects = []

      formElements.forEach((formElement): void => {
        const formGroup = getClosestElement(formElement, 'form-group') as HTMLElement
        const formElementType = formElement.type
        const validationRules = {} as { [key: string]: { message: string } }

        if (isValidJson(formGroup.dataset.msg) === true) {
          const errorMessages = JSON.parse(formGroup.dataset.msg)
          const emptyError = errorMessages.filter((errorMessage: { code: string }) => errorMessage.code === IS_EMPTY)[0]
          const emailError = errorMessages.filter((errorMessage: { code: string }) => errorMessage.code === IS_INVALID_EMAIL)[0]

          if (emptyError && formElementType !== 'file') {
            validationRules.notEmpty = {
              message: emptyError.message
            }
          }

          if (emptyError && formElementType === 'file') {
            validationRules.noFile = {
              message: emptyError.message
            }
          }

          if (emailError) {
            validationRules.email = {
              message: emailError.message
            }
          }

          const noValidationMessagesProvided = Object.keys(validationRules).length === 0 &&
            (formElement.required === true || formElement.getAttribute('data-required') === 'true')

          if (noValidationMessagesProvided === true) {
            if (formElementType !== 'file') {
              validationRules.notEmpty = { message: '' }
            } else {
              validationRules.noFile = { message: '' }
            }
          }
        }

        formValidationObjects.push(
          {
            element: formElement,
            field: formElement.name,
            rules: validationRules
          }
        )

        let fieldChanged = false
        if (
          formElementType === 'radio' ||
          formElementType === 'checkbox' ||
          formElementType === 'select-one'
        ) {
          formElement.addEventListener('change', (): void => {
            this.handleFormElementInput(form, formId, formElement)
          })
        } else {
          formElement.addEventListener('change', (): void => {
            fieldChanged = true
          })

          formElement.addEventListener('input', (): void => {
            this.removeErrorsForFormElement(form, formElement.name)
          })

          formElement.addEventListener('blur', (): void => {
            // Validate field only if value has changed
            if (fieldChanged === false) {
              return
            }

            this.handleFormElementInput(form, formId, formElement)
          })
        }
      })

      form.setAttribute('novalidate', 'novalidate')
      this.initFormValidationSuite(formId, formValidationObjects)

      form.addEventListener('submit', (event): void => {
        this.handleFormSubmit(form, formId, event)
      })

      const submitButton = getElementChild(form, 'form-submit')
      submitButton?.addEventListener('click', (): void => {
        submitButton.classList.add(IS_LOADING)

        setTimeout(() => {
          submitButton.classList.remove(IS_LOADING)
        }, 2000)
      })
    })
  }

  private addError (container, formElements, errors) {
    const errorContainer = getElementChild(container, 'form-error') as HTMLElement

    if (errorContainer === null) {
      return
    }

    errorContainer.innerText = ''
    errors.forEach((error) => {
      errorContainer.innerText += error + '\n'
    })

    formElements.forEach((formElement) => {
      formElement.setAttribute('aria-invalid', 'true')
    })
  }

  private getFormDataAndRemoveFormErrors (form: HTMLFormElement) {
    const formElements = getElementChildren(form, 'form-element')

    formElements.forEach((formElement) => {
      this.removeErrorsForFormElement(form, formElement.name)
    })

    return Object.fromEntries((new FormData(form)).entries())
  }

  private getFormValidationSuiteByFormId (formIdentifier: string) {
    return this.validationSuites.find((validationSuite): boolean => {
      return validationSuite.formId === formIdentifier
    })
  }

  private handleFormElementInput (form: HTMLFormElement, formId: string, formElement: HTMLInputElement): void {
    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)

    if (typeof formValidationSuiteObject === 'undefined') {
      return
    }

    let formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance
    const fieldValidationObject = Object.fromEntries((new FormData(form)).entries())
    formValidationSuite.reset()

    formValidationSuite = formValidationSuite(
      fieldValidationObject,
      formElement.name
    )

    if (formValidationSuite.hasErrors()) {
      this.showErrors(form, formValidationSuite.getErrors())
    } else {
      this.removeErrorsForFormElement(form, formElement.name)
    }
  }

  private handleFormSubmit (form, formId: string, event: Event): void {
    const formValidationSuiteObject = this.getFormValidationSuiteByFormId(formId)

    if (typeof formValidationSuiteObject === 'undefined') {
      return
    }

    const formValidationSuite = formValidationSuiteObject.formValidationSuiteInstance(
      this.getFormDataAndRemoveFormErrors(form)
    )

    if (formValidationSuite.hasErrors() === false) {
      return
    }

    event.preventDefault()
    this.showErrors(form, formValidationSuite.getErrors())
  }

  private initFormValidationSuite (formIdentifier, formValidationObjects): void {
    const validationSuite = create((data = {}, currentField): void => {
      only(currentField)

      // vest requires a stable order of test registrations across runs, so every
      // conditional test has to be wrapped in omitWhen instead of being skipped
      formValidationObjects.forEach((formValidationObject) => {
        // Fields hidden by a display condition are validated neither on the server nor here
        const isHidden = getClosestElement(formValidationObject.element, 'form-ctr')?.hidden === true

        omitWhen(isHidden, () => {
          if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'notEmpty')) {
            test(formValidationObject.field, formValidationObject.rules.notEmpty.message, () => {
              enforce(data[formValidationObject.field]).isNotBlank()
            })
          }

          if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'noFile')) {
            // Server side added files present?
            const submittedFile = document.querySelector('[name="' + formValidationObject.field + '[submittedFile][resourcePointer]"]')

            omitWhen(submittedFile !== null, () => {
              test(formValidationObject.field, formValidationObject.rules.noFile.message, () => {
                enforce(data[formValidationObject.field]).isSingleFile()
              })
            })
          }

          if (Object.prototype.hasOwnProperty.call(formValidationObject.rules, 'email')) {
            omitWhen(!data[formValidationObject.field], () => {
              test(formValidationObject.field, formValidationObject.rules.email.message, () => {
                enforce(data[formValidationObject.field]).isEmail()
              })
            })
          }
        })
      })
    })

    const presentValidationSuite = this.validationSuites.find((validationSuite) => {
      return validationSuite.formId === formIdentifier
    })

    if (typeof presentValidationSuite === 'undefined') {
      this.validationSuites.push(
        {
          formId: formIdentifier,
          formValidationSuiteInstance: validationSuite
        }
      )
    }
  }

  private removeErrorsForFormElement (form: HTMLFormElement, formElementName: string): void {
    const formElements = form.querySelectorAll('[data-js="form-element"][name="' + formElementName + '"]')

    if (formElements.length === 0) {
      return
    }

    formElements.forEach((formElement: HTMLElement): void => {
      formElement.removeAttribute('aria-invalid')
    })

    const container = getClosestElement(formElements[0] as HTMLElement, 'form-ctr')
    container.classList.remove(IS_BAD)

    const errorContainer = getElementChild(container, 'form-error') as HTMLElement
    errorContainer.innerText = ''
  }

  private showErrors (form, errorsByFormField) {
    for (const [formElementName, errorMessages] of Object.entries(errorsByFormField)) {
      this.showErrorsForFormElement(form, formElementName, errorMessages)
    }

    const firstErrorElementName = Object.entries(errorsByFormField)[0][0]
    const firstErrorElement = form.querySelector('[data-js="form-element"][name="' + firstErrorElementName + '"]')

    if (firstErrorElement !== null) {
      let elementToScrollTo = firstErrorElement

      if (window.getComputedStyle(elementToScrollTo).display === 'none') {
        elementToScrollTo = elementToScrollTo.parentElement
      }

      elementToScrollTo?.scrollIntoView(
        {
          block: 'center'
        }
      )

      elementToScrollTo?.focus()
    }
  }

  private showErrorsForFormElement (form, formElementName: string, errorMessages): void {
    const formElements = form.querySelectorAll('[data-js="form-element"][name="' + formElementName + '"]')

    if (formElements.length === 0) {
      return
    }

    const container = getClosestElement(formElements[0], 'form-ctr')
    container.classList.add(IS_BAD)

    this.addError(container, formElements, errorMessages)
  }
}
